import { AncetItem } from "../model/types";

type QuestionConfig = {
  title: string;
  fullTitle?: string;
  type: "single" | "multi" | "text";
  options?: Record<string, string>;
  parser?: (answer: string) => string;
};

const stripPrefix = (value: string) =>
  value.replace(/^[^0-9A-Za-zА-Яа-яЁё№]+/, "").trim();

const multiParser =
  (options: Record<string, string>) => (answer: string): string => {
    const parts = answer.split("%").map((part) => part.trim()).filter(Boolean);

    if (!parts.length) {
      return "—";
    }

    return parts
      .map((part) => stripPrefix(options[part] || part))
      .filter(Boolean)
      .join(", ");
  };

const singleParser =
  (options: Record<string, string>) => (answer: string): string => {
    if (!answer) {
      return "—";
    }

    return stripPrefix(options[answer] || answer);
  };

const questions: Record<number, QuestionConfig> = {
  1: {
    title: "Цель",
    fullTitle: "🔥 Какая у тебя цель?",
    type: "multi",
    options: {
      1: "Сбросить вес",
      2: "Развитие гибкости",
      3: "Восстановление после травмы",
      4: "Держать в тонусе",
      5: "Укрепить здоровье",
      6: "Набрать мышечную массу",
    },
  },
  2: {
    title: "Пол",
    fullTitle: "🙋‍♂️ Укажи свой пол",
    type: "single",
    options: {
      1: "👨 М",
      2: "👩 Ж",
      3: "🌈 Предпочитаю не отвечать",
    },
  },
  3: {
    title: "Главный вызов",
    fullTitle: "⭐ Выбери самый главный вызов в фитнесе!",
    type: "multi",
    options: {
      1: "💥 Поддерживать мотивацию",
      2: "⏳ Находить время",
      3: "🚀 Начать",
      4: "📈 Преодолеть плато/выйти на новый уровень",
      5: "✨ Другое",
    },
  },
  4: {
    title: "Занятия спортом раньше",
    fullTitle: "💪 Как часто ты занимался спортом последние полгода?",
    type: "single",
    options: {
      1: "🛌 Совсем не занимаюсь",
      2: "🌱 Редко/нерегулярно",
      3: "🏋️ 2-3 раза в неделю",
      4: "🔥 Практически ежедневно",
    },
  },
  5: {
    title: "Дни тренировок",
    fullTitle:
      "🗓️ По каким дням неделе тебе удобно тренироваться? (позже можно буде изменить или перенести)",
    type: "multi",
    options: {
      1: "Понедельник",
      2: "Вторник",
      3: "Среда",
      4: "Четверг",
      5: "Пятница",
      6: "Суббота",
      7: "Воскресенье",
    },
  },
  6: {
    title: "Особенности программы",
    fullTitle:
      "Расскажи, чего бы ты хотел(а) видеть в своей программе тренировок?",
    type: "text"
  },
  7: {
    title: "Особенности здоровья",
    fullTitle: "🩺 Есть ли у тебя особенности?",
    type: "multi",
    options: {
      1: "🦵 Травмы суставов/связок",
      2: "🧠 Психологическое состояние",
      3: "🤰 Беременность",
      4: "✏️ Другое",
      5: "Нет",
    },
  },
  8: {
    title: "Опыт в спорте",
    fullTitle: "🏃 Каким спортом ты уже занимаешься?",
    type: "multi",
    options: {
      1: "Бег/Ходьба",
      2: "Силовые тренировки",
      3: "Йога/Пилатес",
      4: "Плавание",
      5: "Велоспорт",
      6: "Танцы",
      7: "Единоборства",
      8: "Другое",
      9: "Не занимаюсь",
    },
  },
  9: {
    title: "Место тренировок",
    fullTitle: "🏠 Где ты будешь тренироваться?",
    type: "multi",
    options: {
      1: "🧘 Студия (йога, пилатес)",
      2: "🛋️ Дома",
      3: "🏋️ Спортзал",
      4: "🌳 На улице",
    },
  },
  10: {
    title: "Возраст, вес, рост",
    fullTitle: "📝 Введите ваш возраст, вес и рост",
    type: "text",
    parser: (answer: string) => {
      if (!answer) {
        return "—";
      }

      const [age, weight, height] = answer
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);

      const segments = [
        age ? `Возраст: ${age}` : null,
        weight ? `Вес: ${weight}` : null,
        height ? `Рост: ${height}` : null,
      ].filter(Boolean);

      return segments.length ? segments.join(" · ") : answer;
    },
  },
  107: {
    title: "Зона травмы",
    fullTitle: "🩹 Какой именно сустав/связка травмированы?",
    type: "multi",
    options: {
      1: "Шея",
      2: "Плечи / Плечевой пояс",
      3: "Руки (Верхняя часть/Плечо, Предплечье)",
      4: "Спина (Верхняя, Нижняя/Поясница)",
      5: "Грудь",
      6: "Живот (Пресс)",
      7: "Таз / Ягодицы",
      8: "Бедра (Передняя часть, Задняя часть, Внутренняя часть)",
      9: "Голени (Икры, Передняя часть)",
    },
  },
  1001: {
    title: "Сложно выбрать упражнение",
    fullTitle: "Мне сложно выбрать подходящее упражнение под себя",
    type: "single",
    options: {
      0: "Нет",
      1: "Да",
    },
  },
  1002: {
    title: "Упражнения сложные или скучные",
    fullTitle: "Я считаю упражнения слишком сложными или скучными и легко могу сдаться.",
    type: "single",
    options: {
      0: "Нет",
      1: "Да",
    },
  },
  1003: {
    title: "Демотивация без результатов",
    fullTitle: "Я чувствую себя демотивированным, когда не вижу результатов.",
    type: "single",
    options: {
      0: "Нет",
      1: "Да",
    },
  },
  1004: {
    title: "Награда за достижение цели",
    fullTitle: "Как вы собираетесь себя наградить после достижения ваших целей?",
    type: "single",
    options: {
      1: "Примерить новую одежду",
      2: "Купить подарок",
      3: "Путешествия в места мечты",
      4: "Пост онлайн",
    },
  },
  1005: {
    title: "Готовность к дефициту/профициту калорий",
    fullTitle: "Готовы ли вы придерживаться дефицита/профицита калорий?",
    type: "single",
    options: {
      0: "Нет",
      1: "Да",
    },
  },
  1006: {
    title: "Помощь в программе питания",
    fullTitle: "Нужна ли вам помощь в составлении персональной программы питания?",
    type: "single",
    options: {
      0: "Нет",
      1: "Да",
    },
  },
  1007: {
    title: "Номер телефона",
    fullTitle: "Номер телефона",
    type: "text",
  },
  1101: {
    title: "Желаемая форма тела",
    fullTitle: "Желаемая форма тела",
    type: "text",
  },
  1102: {
    title: "Текущая форма тела",
    fullTitle: "Текущая форма тела",
    type: "text",
  },
  2001: {
    title: "Посмотрел ли отзывы",
    fullTitle: "Посмотрел ли отзывы",
    type: "text",
  },
  2002: {
    title: "Показана ли интерпретация",
    fullTitle: "Показана ли интерпретация",
    type: "text",
  },
};

const QUESTION_ORDER = [
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  107,
  8,
  9,
  10,
  1001,
  1002,
  1003,
  1004,
  1005,
  1006,
  1007,
  1101,
  1102,
  2001,
  2002,
] as const;

export type AncetViewItem = {
  title: string;
  fullTitle: string;
  value: string;
  questNum: number;
};

export const transformAncet = (items: AncetItem[] | null | undefined) => {
  if (!items?.length) {
    return [];
  }

  const itemsByQuestion = new Map<number, AncetItem>();
  items.forEach((item) => {
    if (questions[item.quest_num]) {
      itemsByQuestion.set(item.quest_num, item);
    }
  });

  const result: AncetViewItem[] = [];

  QUESTION_ORDER.forEach((questNum) => {
    const question = questions[questNum];
    const item = itemsByQuestion.get(questNum);

    if (!question || !item) {
      return;
    }

    let value = "—";

    if (question.type === "text" && question.parser) {
      value = question.parser(item.answer);
    } else if (question.options) {
      const parser =
        question.type === "multi"
          ? multiParser(question.options)
          : singleParser(question.options);

      value = parser(item.answer);
    } else if (question.parser) {
      value = question.parser(item.answer);
    } else {
      value = item.answer || "—";
    }

    result.push({
      title: question.title,
      fullTitle: question.fullTitle ?? question.title,
      value,
      questNum,
    });
  });

  return result;
};

